"use client";

import * as React from "react";

import { cx } from "../../lib/cx";
import { useClickAfuera, useFlotante } from "../../lib/flotante";
import { Avatar } from "../core/Avatar";
import { Icon, type IconName } from "../core/Icon";
import s from "./Menu.module.css";

/** El color del punto de una opción: el estado al que lleva. */
export type MenuDotTone = "warning" | "brand" | "neutral" | "success" | "danger";

export interface MenuItem {
  label: string;
  /** Un icono a la izquierda. */
  icon?: IconName;
  /** Un punto de estado a la izquierda: "Marcar en curso" con el violeta de "en curso". */
  dot?: MenuDotTone;
  /** El nombre de una persona: sus iniciales a la izquierda (asignar a alguien). */
  avatar?: string;
  /** Texto chico y gris a la derecha: cuántas consultas tiene esa propiedad. */
  detail?: string;
  /** La opción vigente: fondo accent-50 y tilde. Con alguna `selected` el menú es un grupo de radios. */
  selected?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuProps extends Omit<React.ComponentPropsWithoutRef<"span">, "children"> {
  /** Nombre accesible del menú: "Cambiar estado", "Asignar a". */
  label: string;
  /** El control que lo abre: un `Badge as="button"`, un `Button`, un `IconButton`. Recibe el click y los `aria-*`. */
  trigger: React.ReactElement<Record<string, unknown>>;
  items: MenuItem[];
  /** A qué borde del disparador se pega el panel. Default `left`. */
  align?: "left" | "right";
  /** Ancho mínimo del panel, en px; si el disparador es más ancho, mide lo que él. Default 180. */
  minWidth?: number;
}

const ALTO_PANEL = 320;

/**
 * Un menú de acciones u opciones que cuelga de un control: cambiar el estado
 * de una fila, asignar a alguien, elegir un filtro con detalle. El panel se
 * dibuja en fijo (ver `useFlotante`), así que funciona adentro de una tabla
 * con `overflow` y de un `Dialog`.
 *
 * Los clicks adentro no salen del menú: dentro de una fila clickeable, abrir
 * el menú o elegir una opción no abre la fila.
 *
 * Teclado: Enter, espacio y flecha abajo abren y llevan el foco a la primera
 * opción (o a la elegida); flechas, Home y End mueven; Enter y espacio eligen;
 * Escape cierra y devuelve el foco al disparador; Tab cierra y sigue.
 */
export function Menu({ label, trigger, items, align = "left", minWidth = 180, className, onClick, ...rest }: MenuProps) {
  const id = React.useId();
  const idPanel = `${id}-menu`;
  const [abierto, setAbierto] = React.useState(false);
  const raiz = React.useRef<HTMLSpanElement>(null);
  const panel = React.useRef<HTMLDivElement>(null);

  const flotante = useFlotante(raiz, abierto, { altoMax: ALTO_PANEL, anchoMin: minWidth, alinear: align === "right" ? "derecha" : "izquierda" });
  const cerrarSinFoco = React.useCallback(() => setAbierto(false), []);
  useClickAfuera(raiz, abierto, cerrarSinFoco);

  const disparador = () => raiz.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]') ?? null;

  function cerrar(devolverFoco = true) {
    setAbierto(false);
    if (devolverFoco) disparador()?.focus();
  }

  // Al abrir, el foco va a la opción vigente o a la primera.
  React.useEffect(() => {
    if (!abierto || !panel.current) return;
    const opciones = panel.current.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)');
    const elegida = Array.from(opciones).find((o) => o.getAttribute("aria-checked") === "true");
    (elegida ?? opciones[0])?.focus();
  }, [abierto]);

  function mover(paso: 1 | -1 | "inicio" | "fin") {
    if (!panel.current) return;
    const opciones = Array.from(panel.current.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)'));
    if (opciones.length === 0) return;
    const actual = opciones.findIndex((o) => o === document.activeElement);
    let destino: number;
    if (paso === "inicio") destino = 0;
    else if (paso === "fin") destino = opciones.length - 1;
    else destino = actual < 0 ? 0 : (actual + paso + opciones.length) % opciones.length;
    opciones[destino]?.focus();
  }

  const onKeyPanel = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") mover(1);
    else if (e.key === "ArrowUp") mover(-1);
    else if (e.key === "Home") mover("inicio");
    else if (e.key === "End") mover("fin");
    else if (e.key === "Escape") cerrar();
    else if (e.key === "Tab") {
      cerrar(false);
      return;
    } else return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onKeyDisparador = (e: React.KeyboardEvent<HTMLElement>) => {
    const original = trigger.props.onKeyDown;
    if (typeof original === "function") original(e);
    if (e.defaultPrevented) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setAbierto(true);
    } else if (e.key === "Escape" && abierto) {
      e.preventDefault();
      cerrar();
    }
  };

  const onClickDisparador = (e: React.MouseEvent<HTMLElement>) => {
    const original = trigger.props.onClick;
    if (typeof original === "function") original(e);
    if (e.defaultPrevented) return;
    setAbierto((v) => !v);
  };

  const conRadio = items.some((i) => i.selected !== undefined);

  return (
    <span
      ref={raiz}
      className={cx(s.raiz, className)}
      onClick={(e) => {
        onClick?.(e);
        e.stopPropagation();
      }}
      {...rest}
    >
      {React.cloneElement(trigger, {
        onClick: onClickDisparador,
        onKeyDown: onKeyDisparador,
        "aria-haspopup": "menu",
        "aria-expanded": abierto,
        "aria-controls": abierto ? idPanel : undefined,
      })}
      {abierto ? (
        <div
          ref={panel}
          id={idPanel}
          role="menu"
          aria-label={label}
          className={s.panel}
          data-arriba={flotante.arriba ? "" : undefined}
          style={flotante.style}
          onKeyDown={onKeyPanel}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role={conRadio ? "menuitemradio" : "menuitem"}
              aria-checked={conRadio ? Boolean(item.selected) : undefined}
              tabIndex={-1}
              disabled={item.disabled}
              className={s.item}
              data-selected={item.selected || undefined}
              onClick={() => {
                cerrar();
                item.onSelect();
              }}
            >
              {item.avatar !== undefined ? (
                <Avatar nombre={item.avatar} size="xs" decorative />
              ) : item.dot ? (
                <span className={s.punto} data-tone={item.dot} aria-hidden="true" />
              ) : item.icon ? (
                <Icon name={item.icon} size={16} />
              ) : null}
              <span className={s.texto}>{item.label}</span>
              {item.detail ? <span className={s.detalle}>{item.detail}</span> : null}
              {item.selected ? (
                <span className={s.tilde}>
                  <Icon name="check" size={14} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}
